'use client';

import { useEffect, useState } from 'react';
import { useAuthSession } from '@/app/components/auth/useAuthSession';
import { ccpaRequestTypes } from '@/lib/legal';
import { strings } from '@/content/strings';

type DataRequestFormProps = {
  defaultType?: string;
  allowTypeChange?: boolean;
  onSuccess?: () => void;
};

export default function DataRequestForm({
  defaultType = 'ACCESS',
  allowTypeChange = true,
  onSuccess,
}: DataRequestFormProps) {
  const [requestType, setRequestType] = useState(defaultType);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const { data: session } = useAuthSession();
  const lockedEmail = !!session?.user?.email;

  useEffect(() => {
    if (!session?.user?.email) return;
    setEmail(session.user.email);
  }, [session?.user?.email]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/legal/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestType,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          details: details.trim(),
          company: honeypot.trim(),
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || strings.screens.legal.dataRequestForm.submitError);
      } else {
        setSuccess(true);
        if (requestType === 'OPT_OUT') {
          setOptOutCookie();
        }
        onSuccess?.();
      }
    } catch (err) {
      setError(strings.screens.legal.dataRequestForm.submitError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {success ? (
        <div className="rounded-lg border border-border bg-ghost p-4 text-sm text-foreground">
          {strings.screens.legal.dataRequestForm.success}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">
                {strings.screens.legal.dataRequestForm.fullName}
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {strings.screens.legal.dataRequestForm.email}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  if (!lockedEmail) setEmail(e.target.value);
                }}
                readOnly={lockedEmail}
                aria-readonly={lockedEmail}
                className={`w-full rounded-lg border border-border px-3 py-2 text-sm text-foreground ${
                  lockedEmail ? 'bg-ghost/70 text-muted-foreground' : 'bg-background'
                }`}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">
                {strings.screens.legal.dataRequestForm.phone}
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {strings.screens.legal.dataRequestForm.requestType}
              </label>
              <select
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
                disabled={!allowTypeChange}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-70"
              >
                {ccpaRequestTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              {strings.screens.legal.dataRequestForm.details}
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>

          <div className="sr-only">
            <label htmlFor="company">{strings.screens.legal.dataRequestForm.company}</label>
            <input
              id="company"
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {loading ? strings.screens.legal.dataRequestForm.submitting : strings.screens.legal.dataRequestForm.submit}
          </button>
          {error && <div className="text-sm text-destructive">{error}</div>}
        </>
      )}
    </form>
  );
}

function setOptOutCookie() {
  const maxAge = 60 * 60 * 24 * 365;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `ccpa_opt_out=1; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  document.cookie = `analytics_consent=declined; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  document.cookie = `marketing_consent=declined; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}
